import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { WorkflowEtape } from '../models/models';

interface WorkflowVisualizerProps {
  etapes: WorkflowEtape[];
}

const NODE_W = 150;
const NODE_H = 60;
const COL_GAP = 90;
const ROW_GAP = 16;
const PAD_X = 16;
const PAD_Y = 16;

interface NodePos {
  code: string;
  libelle: string;
  x: number;
  y: number;
}

interface EdgePos {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

const WorkflowVisualizer: React.FC<WorkflowVisualizerProps> = ({ etapes }) => {
  const { nodePositions, edgePositions, svgWidth, svgHeight } = useMemo(() => {
    if (!etapes.length) return { nodePositions: [], edgePositions: [], svgWidth: 0, svgHeight: 0 };

    // Construire la map code → libelle
    const nodeMap = new Map<string, string>();
    etapes.forEach(e => {
      if (!nodeMap.has(e.doc_source_code))
        nodeMap.set(e.doc_source_code, e.source_libelle || e.doc_source_code);
      if (!nodeMap.has(e.doc_cible_code))
        nodeMap.set(e.doc_cible_code, e.cible_libelle || e.doc_cible_code);
    });

    // BFS pour assigner les niveaux (colonne)
    const allCibles = new Set(etapes.map(e => e.doc_cible_code));
    const roots = Array.from(nodeMap.keys()).filter(n => !allCibles.has(n));

    const levels = new Map<string, number>();
    const queue: { node: string; level: number }[] = roots.map(r => ({ node: r, level: 0 }));

    // Si pas de racine (cycle ou tous cibles), partir du premier
    if (!queue.length && nodeMap.size > 0) {
      queue.push({ node: Array.from(nodeMap.keys())[0], level: 0 });
    }

    while (queue.length) {
      const { node, level } = queue.shift()!;
      const current = levels.get(node) ?? -1;
      if (level > current) {
        levels.set(node, level);
        etapes
          .filter(e => e.doc_source_code === node)
          .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0))
          .forEach(e => queue.push({ node: e.doc_cible_code, level: level + 1 }));
      }
    }

    // Grouper par colonne et trier par ordre d'apparition dans les étapes
    const byLevel = new Map<number, string[]>();
    // Ordonner les nœuds par ordre d'apparition pour placement stable
    const allCodes = Array.from(nodeMap.keys());
    allCodes.forEach(code => {
      const lvl = levels.get(code) ?? 0;
      if (!byLevel.has(lvl)) byLevel.set(lvl, []);
      byLevel.get(lvl)!.push(code);
    });

    // Calculer les positions
    const positions = new Map<string, { x: number; y: number }>();
    byLevel.forEach((codes, lvl) => {
      codes.forEach((code, idx) => {
        positions.set(code, {
          x: PAD_X + lvl * (NODE_W + COL_GAP),
          y: PAD_Y + idx * (NODE_H + ROW_GAP),
        });
      });
    });

    // Dimensions du SVG
    let maxX = 0;
    let maxY = 0;
    positions.forEach(({ x, y }) => {
      if (x + NODE_W > maxX) maxX = x + NODE_W;
      if (y + NODE_H > maxY) maxY = y + NODE_H;
    });

    const nodePositions: NodePos[] = Array.from(nodeMap.entries()).map(([code, libelle]) => ({
      code,
      libelle,
      x: positions.get(code)?.x ?? 0,
      y: positions.get(code)?.y ?? 0,
    }));

    const edgePositions: EdgePos[] = etapes
      .map(e => {
        const src = positions.get(e.doc_source_code);
        const dst = positions.get(e.doc_cible_code);
        if (!src || !dst) return null;
        return {
          x1: src.x + NODE_W,
          y1: src.y + NODE_H / 2,
          x2: dst.x,
          y2: dst.y + NODE_H / 2,
        };
      })
      .filter(Boolean) as EdgePos[];

    return {
      nodePositions,
      edgePositions,
      svgWidth: maxX + PAD_X,
      svgHeight: maxY + PAD_Y,
    };
  }, [etapes]);

  if (!etapes.length) return null;

  return (
    <Box
      sx={{
        overflowX: 'auto',
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1,
        mb: 2,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
        Aperçu du workflow
      </Typography>
      <Box sx={{ position: 'relative', width: svgWidth, height: svgHeight, minHeight: NODE_H + PAD_Y * 2 }}>
        {/* Flèches SVG */}
        <svg
          style={{ position: 'absolute', top: 0, left: 0, width: svgWidth, height: svgHeight, pointerEvents: 'none' }}
        >
          <defs>
            <marker id="wf-arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#1976d2" />
            </marker>
          </defs>
          {edgePositions.map((e, i) => {
            const cx = (e.x1 + e.x2) / 2;
            return (
              <path
                key={i}
                d={`M ${e.x1} ${e.y1} C ${cx} ${e.y1}, ${cx} ${e.y2}, ${e.x2} ${e.y2}`}
                fill="none"
                stroke="#1976d2"
                strokeWidth={2}
                markerEnd="url(#wf-arrow)"
              />
            );
          })}
        </svg>

        {/* Nœuds */}
        {nodePositions.map(n => (
          <Paper
            key={n.code}
            elevation={2}
            sx={{
              position: 'absolute',
              left: n.x,
              top: n.y,
              width: NODE_W,
              height: NODE_H,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1,
              borderRadius: 2,
              borderTop: '3px solid',
              borderColor: 'primary.main',
              userSelect: 'none',
            }}
          >
            <Typography variant="caption" fontWeight="bold" color="primary.main" lineHeight={1.2}>
              {n.code}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: NODE_W - 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}
            >
              {n.libelle}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default WorkflowVisualizer;
